import { I18n } from '@calendly/i18n'
import { FullStoryMask } from '@calendly/ui/components/fullstory-privacy'
import { Options, SearchInput, Select } from '@calendly/ui/components/select'
import { useCallback, useLayoutEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { filterResults } from 'actions/home_bar/scope_dropdown'
import { useMyCalendlyDropdownEventProps } from 'components/empty_state'
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events'
import { useHomeTabs } from 'hooks/home/tabs'
import { filterTermSelector } from 'selectors/home_bar'
import { isNoTouchDevice } from 'services/touch_device_detection'

import Body from './body'
import ScopeDropdownControl from './control'
import { decodeScopeValue, encodeScopeValue } from './scope_values'
import { useMeetingsFilterScopePopoverPortal } from './use_meetings_filter_scope_popover_portal'

const ScopeDropdownSearchInput = () => {
  const dispatch = useDispatch()
  const filterTerm = useSelector(filterTermSelector)

  return (
    <FullStoryMask>
      <SearchInput
        searchTerm={filterTerm}
        setSearchTerm={(value) => dispatch(filterResults(value))}
        searchPlaceholder={I18n.t('publisher.event_types.filter')}
        aria-label={I18n.t('publisher.event_types.filter')}
        autoFocus={isNoTouchDevice()}
      />
    </FullStoryMask>
  )
}

const ScopeDropdown = ({
  fitContent = false,
  showSearch = true,
  useMeetingsFilterPortal = false,
  className,
  controlClassName,
}) => {
  const dispatch = useDispatch()
  const controlRef = useRef(null)
  const { getTabScopeV1, setTabScopeV1 } = useHomeTabs()
  const { type, id } = getTabScopeV1()
  const selectedValue = encodeScopeValue(type, id)

  useMeetingsFilterScopePopoverPortal(useMeetingsFilterPortal)

  const track = useTrack()
  const { pathname } = useLocation()
  const properties = useMyCalendlyDropdownEventProps(pathname)

  const trackDropdownOpen = useCallback(() => {
    if (!properties) return

    track({
      name: TRACKING_EVENT_NAMES.DROPDOWN_SELECTED,
      properties
    })
  }, [properties, track])

  const handleScopeChange = (value) => {
    const { type: scopeType, id: scopeId } = decodeScopeValue(value)

    dispatch({
      type: 'SCOPE_DROPDOWN_UPDATE_FILTER_TERM',
      payload: ''
    })
    dispatch(setTabScopeV1({ type: scopeType, id: scopeId }))
  }

  useLayoutEffect(() => {
    const control = controlRef.current
    if (!control) return undefined

    const handleOpen = () => {
      if (control.getAttribute('aria-expanded') === 'true') return

      trackDropdownOpen()
    }

    control.addEventListener('click', handleOpen, { capture: true })

    return () => control.removeEventListener('click', handleOpen, { capture: true })
  }, [trackDropdownOpen])

  return (
    <Select
      data-component="scope-switcher"
      data-calendly-label="scope-switcher"
      value={selectedValue}
      onChange={handleScopeChange}
      selectedIndicator="highlight"
      className={className}
      spacingTop={controlClassName ? '0' : undefined}
      spacingBottom={controlClassName ? '0' : undefined}
    >
      <ScopeDropdownControl
        ref={controlRef}
        fitContent={fitContent}
        showScopeIcon={useMeetingsFilterPortal}
        className={controlClassName}
      />
      <Options
        data-component="scope-dropdown-menu"
        data-testid="scope-dropdown-options"
        searchInput={showSearch ? <ScopeDropdownSearchInput /> : undefined}
      >
        <FullStoryMask>
          <Body />
        </FullStoryMask>
      </Options>
    </Select>
  )
}

export default ScopeDropdown
